import type { DraggedAssets } from "@/client/core/Core.api";
import type { LabsMessageToLive } from "@/client/core/platform-types/labs.api";
import {
    LabsMessageToLiveType,
    LabsMessageToPlatformType,
} from "@/client/core/platform-types/labs.api";
import { registerRequestAuthToken, setAuthToken } from "@/client/core/services/auth-token.signal";
import {
    markResourceExported,
    registerSidepanelSend,
} from "@/client/core/services/sidepanel-export";
import { isPluginParentOrigin } from "@/client/core/services/plugin-parent-origin";
import { setHostBottomInset } from "@/client/utils/hostInset";

export interface SidepanelBridgeHandlers {
    /**
     * Catalog drag metadata forwarded by the host as a pre-drop hint, with the
     * transform the host applied when it is handing over a re-render.
     */
    onAssetsDragStarted: (dragged: DraggedAssets) => void;
}

/**
 * Bridge for sidepanel hosts (the Splice Sounds plugin's Labs panel). Accepts
 * the host's `PortTransfer` handshake, stores incoming auth tokens (and lets
 * the client request fresh ones), and surfaces catalog drag hints so
 * dropzones can back-fill metadata. Resources, audio, and transport stay
 * local because the app runs in the website environment (see
 * `isSidepanelHost`). Handlers are optional for pages with no drop targets
 * (the home directory), which only need the auth half.
 */
export const createSidepanelAuthBridge = (handlers?: SidepanelBridgeHandlers): void => {
    window.addEventListener("message", (event: MessageEvent): void => {
        if (!isPluginParentOrigin(event.origin)) {
            return;
        }
        const message = event.data as LabsMessageToLive;
        if (message.type !== LabsMessageToLiveType.PortTransfer || event.ports.length === 0) {
            return;
        }

        const port = event.ports[0];
        port.addEventListener("message", (portEvent: MessageEvent): void => {
            const portMessage = portEvent.data as LabsMessageToLive;
            if (portMessage.type === LabsMessageToLiveType.AuthToken) {
                setAuthToken(portMessage.accessToken, portMessage.expiresAt);
            } else if (portMessage.type === LabsMessageToLiveType.AssetsDragStarted) {
                handlers?.onAssetsDragStarted({
                    assets: portMessage.assets,
                    transform: portMessage.transform,
                });
            } else if (portMessage.type === LabsMessageToLiveType.ResourceExported) {
                markResourceExported(portMessage.id);
            } else if (portMessage.type === LabsMessageToLiveType.HostBottomInset) {
                setHostBottomInset(portMessage.px);
            }
        });
        port.start();

        registerRequestAuthToken(() => {
            port.postMessage({ type: LabsMessageToPlatformType.RequestAuthToken });
        });
        registerSidepanelSend((outbound) => port.postMessage(outbound));
    });
};
